import { Modal } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ICountryGroup } from '~/src/dash/api/console-api';
import { createCountryGroup } from '~/src/dash/api/country-group';
import { ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import {
  removeEmojiPrefixFromLabel,
  validateName,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const labelWidth = 108;
const rowHeight = 40;

interface IProps {
  handleSuccess: (id: number) => void;
  isDisabled?: boolean;
  commonRegionGroups: ISelectPreset[];
}

export const CreateRegionModal = ({
  handleSuccess,
  isDisabled = false,
  commonRegionGroups,
}: IProps) => {
  const { session } = useBaseStore();
  const { lists } = useFormBuilderStore();

  const [errors, setErrors] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [selectedCountryIDs, setSelectedCountryIDs] = useState<Set<string>>(new Set([]));
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    unstable_batchedUpdates(() => {
      setErrors([]);
      setName('');
      setSelectedCountryIDs(new Set([]));
      setOpen(false);
    });
  };

  const handleApplyCountries = (value: Set<string>) => {
    setSelectedCountryIDs(value);
  };

  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    } else {
      setIsSaveDisabled(true);
    }

    // validations
    const errorMessages: string[] = [];
    // validate name
    const { sanitizedName, nameError } = validateName(name);
    if (nameError) {
      errorMessages.push(nameError);
    }
    // validate at lease one country selected
    if (isEmpty(selectedCountryIDs)) {
      errorMessages.push('No countries selected.');
    }
    if (!isEmpty(errorMessages)) {
      unstable_batchedUpdates(() => {
        setErrors(errorMessages);
        setIsSaveDisabled(false);
      });
      return;
    }

    createCountryGroup({
      PublisherID: Number(session.publisher.ID),
      Name: sanitizedName,
      CountryIsos: Array.from(selectedCountryIDs)
        .map((id) => id.toUpperCase())
        .join(','),
    } as ICountryGroup)
      .then((result) => {
        if (!isEmpty(result[0]) && result[0].ID !== 0) {
          handleSuccess(result[0].ID);
          handleClose();
        }
      })
      .catch((err) => {
        console.error(err);
        setErrors(['An error has occurred. Please wait and try again.']);
      });
  };

  return (
    <div>
      <TextButton size='small' onClick={handleOpen} disabled={isDisabled}>
        + Region
      </TextButton>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='create-new-region-modal'
        aria-describedby='create-new-region-modal'
      >
        <Panel
          highlighted
          smallVerticalPadding
          extraLargeHorizontalPadding
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '720px',
          }}
        >
          <Rows>
            <Row noHorizontalPadding verticalPadding>
              <Text large bold>
                Create Region
              </Text>
            </Row>
            {/* ERRORS */}
            {!isEmpty(errors) && (
              <Row style={{ padding: '0px 20px 24px' }}>
                <Rows>
                  {errors.map((error) => (
                    <Row noPadding>
                      <Text small color={publicaColors.red}>
                        <Span style={{ paddingRight: spacing.small }}>•</Span>
                        {error}
                      </Text>
                    </Row>
                  ))}
                </Rows>
              </Row>
            )}
            {/* FORMS */}
            <Row noPadding>
              <Rows gap={spacing.small} style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Row noPadding height={rowHeight}>
                  <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        Name
                      </Text>
                    </Col>
                    <Col flex style={{ width: '480px' }}>
                      <TextInput
                        placeholder='New Region'
                        minLength={3}
                        maxLength={99}
                        isAutoFocused
                        handleOnChange={(text) => setName(text)}
                        inputSize='small'
                        width='480px'
                        initialValue={name}
                        key={'RegionName'}
                        inputID='region_name'
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row noPadding height={rowHeight}>
                  <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        Countries
                      </Text>
                    </Col>
                    <Col flex style={{ width: '480px' }}>
                      <IndependentSelect
                        enableLabelTooltip
                        handleApplySelect={handleApplyCountries}
                        listLabel='Countries'
                        options={lists.Countries}
                        placeholder='Select Countries'
                        selectedOptionIDs={selectedCountryIDs}
                        singularLabel='Country'
                        key='Countries'
                        maxWidth={480}
                        extractStringToSort={removeEmojiPrefixFromLabel}
                        presets={commonRegionGroups}
                      />
                    </Col>
                  </Cols>
                </Row>
              </Rows>
            </Row>
            <Row noHorizontalPadding verticalPadding>
              <Cols
                gap={spacing.small}
                style={{ justifyContent: 'flex-end', alignItems: 'center' }}
              >
                <Col>
                  <ButtonV2 small onClick={handleClose}>
                    Cancel
                  </ButtonV2>
                </Col>
                <Col>
                  <ButtonV2 small onClick={handleSave} disabled={isSaveDisabled}>
                    Save
                  </ButtonV2>
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Panel>
      </Modal>
    </div>
  );
};
